import { Currency } from "@redotech/money/currencies";
import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod/zod-enum";
import * as z from "zod/v4";
import {
  CheckoutOptimizationWidgetType,
  ManualProductSourceSchema_,
  PurchaseCheckoutBlockRenderTarget,
} from "./widget";

const baseWidgetSchema = z.object({
  /**
   * Defined `id` means the widget is existing and needs to be updated.
   * Undefined `id` means the widget is new and needs to be created
   */
  id: zExt.objectId().optional(),
  name: z.string(),
  target: createEnum(PurchaseCheckoutBlockRenderTarget).optional(),
  /**
   * @deprecated Widget existence in collection indicates it's enabled
   */
  enabled: z.boolean().optional(),
  /**
   * When true, this widget applies to all checkouts globally (not tied to a specific checkout experience)
   */
  enabledGlobally: z.boolean().optional(),
});

export const checkoutWidgetsSchema = z.object({
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: baseWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.THRESHOLD_UPSELL),
      thresholdAmount: z.number(),
      currency: createEnum(Currency),
      gteThresholdText: z.string(),
      ltThresholdText: z.string(),
      addToCartButtonColor: z.string(),
      addToCartButtonTextColor: z.string(),
      progressBarColor: z.string(),
      excludedProductTags: z.array(z.string()),
      fallbackProductTags: z.array(z.string()),
      manualProductSources: z.array(ManualProductSourceSchema_),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: baseWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF),
      text: z.string(),
      backgroundColor: z.string(),
      iconColor: z.string(),
    })
    .optional(),
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: baseWidgetSchema
    .extend({
      type: z.literal(CheckoutOptimizationWidgetType.CONTENT_BLOCK),
      sections: z.array(
        z.object({
          _id: z.string(),
          title: z.string(),
          description: z.string(),
          imageUrl: z.string().nullish(),
        }),
      ),
    })
    .optional(),
});

/**
 * The global state store for the checkout widgets builder.
 */
const checkoutWidgetsBuilderStateSchema = z.object({
  selectedWidget: createEnum(CheckoutOptimizationWidgetType),
  checkoutWidgets: checkoutWidgetsSchema,
});
export type CheckoutWidgetsBuilderState = z.infer<
  typeof checkoutWidgetsBuilderStateSchema
>;
